// src/components/StockFormModal.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { invalidateCollections } from '../../hooks/useCachedCollection';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const StockFormModal = ({ isOpen, onClose, stockToEdit, onSaved }) => {
    const [formData, setFormData] = useState({
        urunAdi: '',
        stokAdedi: 0,
        alisFiyati: 0,
        satisFiyati: 0,
        stokTuru: '',
        marka: '',
        alinanFirma: '',
        alimTarihi: null
    });
    const [isSaving, setIsSaving] = useState(false);
    const isEditing = Boolean(stockToEdit);

    const toDateOrNull = (timestamp) => {
        if (!timestamp) return null;
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
        }
        if (timestamp instanceof Date) return timestamp;
        return null;
    };

    useEffect(() => {
        if (isOpen) {
            if (isEditing && stockToEdit) {
                setFormData({
                    urunAdi: stockToEdit.urunAdi || '',
                    stokAdedi: stockToEdit.stokAdedi || 0,
                    alisFiyati: stockToEdit.alisFiyati || 0,
                    satisFiyati: stockToEdit.satisFiyati || 0,
                    stokTuru: stockToEdit.stokTuru || '',
                    marka: stockToEdit.marka || '',
                    alinanFirma: stockToEdit.alinanFirma || '',
                    alimTarihi: toDateOrNull(stockToEdit.alimTarihi)
                });
            } else {
                setFormData({
                    urunAdi: '',
                    stokAdedi: 0,
                    alisFiyati: 0,
                    satisFiyati: 0,
                    stokTuru: '',
                    marka: '',
                    alinanFirma: '',
                    alimTarihi: null
                });
            }
        }
    }, [isOpen, stockToEdit, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: ['stokAdedi', 'alisFiyati', 'satisFiyati'].includes(name) ? Number(value) : value
        });
    };

    const handleSubmit = async () => {
        if (!formData.urunAdi) {
            alert('Ürün adı zorunludur.');
            return;
        }

        setIsSaving(true);
        try {
            const dataToSave = {
                ...formData,
                alimTarihi: formData.alimTarihi || null
            };

            if (isEditing) {
                const docRef = doc(db, "stocks", stockToEdit.id);
                await updateDoc(docRef, dataToSave);
            } else {
                await addDoc(collection(db, "stocks"), {
                    ...dataToSave,
                    olusturmaTarihi: serverTimestamp()
                });
            }
            // Stok listesi ve malzeme seçici (satış/servis) anında güncellensin.
            await invalidateCollections(['stocks']);
            onClose();
            // Faz 7: realtime yok — kayıt sonrası liste cache'ini tazele.
            if (onSaved) onSaved();
        } catch (error) {
            console.error("Stok kaydedilirken hata:", error);
            alert("Bir hata oluştu.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</DialogTitle>
            <DialogContent>
                <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        name="urunAdi"
                        label="Ürün Adı"
                        value={formData.urunAdi}
                        onChange={handleChange}
                        required
                        autoFocus
                    />
                    <FormControl fullWidth>
                        <InputLabel>Stok Türü</InputLabel>
                        <Select
                            name="stokTuru"
                            value={formData.stokTuru}
                            label="Stok Türü"
                            onChange={handleChange}
                        >
                            <MenuItem value="">Seçiniz...</MenuItem>
                            <MenuItem value="Sarf">Sarf</MenuItem>
                            <MenuItem value="Cihaz">Cihaz</MenuItem>
                            <MenuItem value="Maske">Maske</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        name="marka"
                        label="Marka"
                        value={formData.marka}
                        onChange={handleChange}
                    />
                    <TextField
                        name="alinanFirma"
                        label="Alınan Firma"
                        value={formData.alinanFirma}
                        onChange={handleChange}
                    />
                    <DatePicker
                        label="Alım Tarihi"
                        value={formData.alimTarihi}
                        onChange={(newValue) => setFormData({ ...formData, alimTarihi: newValue })}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                    <TextField
                        name="stokAdedi"
                        label="Stok Adedi"
                        type="number"
                        value={formData.stokAdedi}
                        onChange={handleChange}
                    />
                    <TextField
                        name="alisFiyati"
                        label="Alış Fiyatı (₺)"
                        type="number"
                        value={formData.alisFiyati}
                        onChange={handleChange}
                    />
                    <TextField
                        name="satisFiyati"
                        label="Satış Fiyatı (₺)"
                        type="number"
                        value={formData.satisFiyati}
                        onChange={handleChange}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose} disabled={isSaving}>İptal</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={24} /> : 'Kaydet'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockFormModal;

